import React, { useEffect, useRef, useState } from 'react'
import { AUTO_QUALITY_LABEL, DEFAULT_PLAYBACK_SPEED, PLAYBACK_SPEEDS } from '@/constants/video'
import { usePlaybackPreferences } from '@/hooks/usePlaybackPreferences'
import { useTranslate } from '@/utils/translate/translate-client'
import { useHlsPlayerUI } from './HlsPlayerUIContext'
import { HlsSettingsPanel } from './HlsSettingsPanel'
import { HlsTooltip } from './HlsTooltip'

/** Settings gear icon — Bitmovin settings.svg (32x32 viewBox). */
const SettingsIcon: React.FC = () => (
  <svg width={24} height={24} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#fff"
      d="M25.6 13.8l-1.4-.3c-.2-.5-.4-1-.6-1.4l.9-1.3c.4-.5.3-1.3-.1-1.7L23 7.7c-.3-.3-.6-.4-1-.4-.3 0-.6.1-.8.3L20 8.5c-.5-.3-1-.5-1.5-.6l-.3-1.4c0-.6-.6-1.1-1.3-1.1H15c-.7 0-1.2.5-1.3 1.1L13.5 8c-.5.2-1 .4-1.4.6l-1.3-.9c-.2-.2-.5-.3-.8-.3-.4 0-.7.1-1 .4L7.8 9.1c-.5.5-.5 1.2-.1 1.7l.9 1.3c-.3.5-.5 1-.6 1.4l-1.4.3c-.7 0-1.2.6-1.2 1.3V17c0 .7.5 1.2 1.1 1.3L8 18.5c.2.5.4 1 .6 1.4l-.9 1.3c-.4.5-.3 1.3.1 1.7l1.4 1.4c.3.3.6.4 1 .4.3 0 .6-.1.8-.3l1.3-.9c.5.2 1 .4 1.4.6l.3 1.4c.1.7.7 1.1 1.3 1.1H17c.7 0 1.2-.5 1.3-1.1l.3-1.4c.5-.2 1-.4 1.4-.6l1.3.9c.2.2.5.3.8.3.4 0 .7-.1 1-.4l1.4-1.4c.5-.5.5-1.2.1-1.7L23.5 20c.3-.5.5-1 .6-1.4l1.4-.3c.7-.1 1.1-.7 1.1-1.3V15c0-.5-.5-1.1-1-1.2zM24.6 16.9l-1.8.4c-.2 0-.4.2-.4.4-.2.6-.4 1.2-.7 1.6-.1.2-.1.4 0 .6l1 1.4v.1l-1.2 1.2h-.1l-.1 0-1.4-1c-.2-.1-.4-.1-.6 0-.5.3-1.1.6-1.6.7-.2.1-.4.2-.4.4L17 24.5c0 .1-.1.1-.1.1h-1.7c-.1 0-.1 0-.1-.1l-.3-1.7c0-.2-.2-.4-.4-.4-.6-.2-1.2-.4-1.6-.7-.1 0-.2-.1-.3-.1-.1 0-.2 0-.3.1l-1.4 1c0 0 0 0-.1 0s0 0-.1 0l-1.2-1.2v-.1l1-1.4c.1-.2.1-.4 0-.6C10 19 9.7 18.4 9.6 17.8c-.1-.2-.2-.4-.4-.4L7.5 17c-.1 0-.1-.1-.1-.1v-1.7c0-.1 0-.1.1-.1l1.7-.2c.2 0 .4-.2.5-.4.2-.6.4-1.2.7-1.6.1-.2.1-.4 0-.6l-1-1.4v-.1l1.2-1.2h.1l.1 0 1.4 1c.2.1.4.1.6 0 .5-.3 1.1-.6 1.6-.7.2-.1.4-.2.4-.4L15 7.5c0-.1.1-.1.1-.1h1.7c.1 0 .1 0 .1.1l.2 1.7c0 .2.2.4.4.5.6.2 1.2.4 1.7.7.2.1.4.1.6 0l1.4-1c0 0 0 0 .1 0s0 0 .1 0l1.2 1.2v.1l-1 1.4c-.1.2-.1.4 0 .6.3.5.6 1.1.7 1.6.1.2.2.4.4.4l1.6.3c.1 0 .1.1.1.1v1.8h0zM16 11.5c-2.5 0-4.5 2-4.5 4.5s2 4.5 4.5 4.5 4.5-2 4.5-4.5-2-4.5-4.5-4.5zM16 18.8c-1.5 0-2.8-1.3-2.8-2.8s1.3-2.8 2.8-2.8 2.8 1.3 2.8 2.8-1.3 2.8-2.8 2.8z"
    />
  </svg>
)

type SettingsPage = 'quality' | 'audio' | 'subtitles' | 'speed'

interface HlsSettingsButtonProps {
  /** When set, opens the panel directly to this sub-page. Cleared by the component after opening. */
  requestedPage?: SettingsPage | null
  onRequestedPageHandled?: () => void
  /** Called when the settings panel opens or closes. */
  onOpenChange?: (isOpen: boolean) => void
}

/**
 * Settings button + panel for the HLS.js player control bar.
 * Opens a settings panel with sub-pages for quality, audio, subtitles, and playback speed.
 */
export const HlsSettingsButton: React.FC<HlsSettingsButtonProps> = ({
  requestedPage,
  onRequestedPageHandled,
  onOpenChange,
}) => {
  const { t } = useTranslate('common')
  const { controller, playbackSpeed } = useHlsPlayerUI()
  const { saveQuality } = usePlaybackPreferences()
  const [open, setOpen] = useState(false)
  const [initialPage, setInitialPage] = useState<SettingsPage | undefined>(undefined)
  const panelRef = useRef<HTMLDivElement>(null)

  // Notify parent when open state changes
  useEffect(() => {
    onOpenChange?.(open)
  }, [open, onOpenChange])

  // Handle external open requests from subtitle/audio buttons.
  // If the panel is already open to the requested page, close it (toggle behavior).
  useEffect(() => {
    if (requestedPage) {
      if (open && initialPage === requestedPage) {
        setOpen(false)
        setInitialPage(undefined)
      } else {
        setInitialPage(requestedPage)
        setOpen(true)
      }
      onRequestedPageHandled?.()
    }
  }, [requestedPage, onRequestedPageHandled, open, initialPage])

  const togglePanel = () => {
    setInitialPage(undefined)
    setOpen((prev) => !prev)
  }

  const closePanel = () => {
    setOpen(false)
    setInitialPage(undefined)
  }

  // Close the panel when clicking outside of the button, the portaled panel,
  // and any settings trigger buttons (subtitle/audio buttons that open the panel).
  useEffect(() => {
    if (!open) return

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      // Check if click is inside the button wrapper
      if (panelRef.current?.contains(target)) return
      // Check if click is inside the portaled settings panel
      const portalPanel = document.getElementById('hlsjs-player')?.querySelector('[data-hls-settings-panel]')
      if (portalPanel?.contains(target)) return
      // Check if click is on a settings trigger button (subtitle/audio buttons)
      if (target instanceof Element && target.closest('[data-hls-settings-trigger]')) return
      setOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  // Read track lists from the controller each time the panel opens
  const audioTracks = open ? controller?.getAvailableAudio() ?? [] : []
  const subtitleTracks = open ? controller?.getSubtitleList() ?? [] : []
  const qualities = open ? controller?.getAvailableQualities() ?? [] : []
  const currentQualityIndex = open ? controller?.getCurrentQualityIndex() ?? -1 : -1
  const activeAudioId = open ? controller?.getCurrentAudioId() ?? '' : ''

  // Current active subtitle track
  const activeSubtitleId = subtitleTracks.find((track) => track.enabled)?.id ?? 'off'

  // Current quality label
  const currentQualityLabel = (() => {
    if (currentQualityIndex < 0) return AUTO_QUALITY_LABEL
    const quality = qualities.find((q) => q.index === currentQualityIndex)
    return quality?.label ?? AUTO_QUALITY_LABEL
  })()

  // Current speed label
  const currentSpeedLabel = playbackSpeed === DEFAULT_PLAYBACK_SPEED ? t('normal', 'Normal') : `${playbackSpeed}x`

  // Current audio label
  const currentAudioLabel = (() => {
    if (audioTracks.length === 0) return t('default', 'Default')
    const active = audioTracks.find((track) => track.id === activeAudioId)
    return active?.label ?? t('default', 'Default')
  })()

  // Current subtitle label
  const currentSubtitleLabel = (() => {
    if (activeSubtitleId === 'off') return t('off', 'Off')
    const active = subtitleTracks.find((track) => track.id === activeSubtitleId)
    return active?.label ?? t('off', 'Off')
  })()

  // Build options for the main menu
  const options = (() => {
    const result = []

    if (qualities.length > 1) {
      result.push({
        id: 'quality',
        label: t('quality', 'Quality'),
        currentValue: currentQualityLabel,
        page: 'quality' as const,
      })
    }

    if (audioTracks.length > 1) {
      result.push({
        id: 'audio',
        label: t('audio', 'Audio'),
        currentValue: currentAudioLabel,
        page: 'audio' as const,
      })
    }

    if (subtitleTracks.length > 0) {
      result.push({
        id: 'subtitles',
        label: t('subtitles', 'Subtitles'),
        currentValue: currentSubtitleLabel,
        page: 'subtitles' as const,
      })
    }

    result.push({
      id: 'speed',
      label: t('speed', 'Speed'),
      currentValue: currentSpeedLabel,
      page: 'speed' as const,
    })

    return result
  })()

  // Build sub-page data
  const pages = {
    quality: {
      title: t('quality', 'Quality'),
      items: [
        { id: 'auto', label: AUTO_QUALITY_LABEL, active: currentQualityIndex < 0 },
        ...qualities
          .sort((a, b) => b.height - a.height)
          .map((q) => ({
            id: String(q.index),
            label: q.label,
            active: q.index === currentQualityIndex,
          })),
      ],
      onSelect: (id: string) => {
        if (id === 'auto') {
          controller?.setQuality(-1)
          saveQuality(AUTO_QUALITY_LABEL)
        } else {
          controller?.setQuality(Number(id))
          const quality = qualities.find((q) => q.index === Number(id))
          if (quality) saveQuality(quality.label)
        }
      },
    },
    audio: {
      title: t('audio', 'Audio'),
      items: audioTracks.map((track) => ({
        id: track.id,
        label: track.label,
        active: track.id === activeAudioId,
      })),
      onSelect: (id: string) => {
        controller?.setAudio(id)
      },
    },
    subtitles: {
      title: t('subtitles', 'Subtitles'),
      items: [
        { id: 'off', label: t('off', 'Off'), active: activeSubtitleId === 'off' },
        ...subtitleTracks.map((track) => ({
          id: track.id,
          label: track.label,
          active: track.id === activeSubtitleId,
        })),
      ],
      onSelect: (id: string) => {
        if (id === 'off') {
          controller?.disableSubTitle()
        } else {
          controller?.enableSubTitle(id)
        }
      },
    },
    speed: {
      title: t('speed', 'Speed'),
      items: PLAYBACK_SPEEDS.map((speed) => ({
        id: String(speed),
        label: speed === DEFAULT_PLAYBACK_SPEED ? t('normal', 'Normal') : `${speed}x`,
        active: playbackSpeed === speed,
      })),
      onSelect: (id: string) => {
        controller?.setPlaybackSpeed(Number(id))
      },
    },
  }

  return (
    <div ref={panelRef} className="relative">
      <button
        type="button"
        className="group relative flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/20"
        onClick={togglePanel}
        aria-label={t('settings', 'Settings')}
      >
        <SettingsIcon />
        <HlsTooltip
          title={t('settings', 'Settings')}
          subtitle={t('settingsDescription', 'Adjust video quality and speed.')}
        />
      </button>
      <HlsSettingsPanel open={open} onClose={closePanel} options={options} pages={pages} initialPage={initialPage} />
    </div>
  )
}
